"use client";

import { useCallback, useState } from "react";
import Link from "next/link";
import Preloader from "@/components/Preloader";
import SmoothScroll from "@/components/SmoothScroll";
import Navigation from "@/components/Navigation";
import Hero from "@/components/Hero";
import Ticker from "@/components/Ticker";
import Collection from "@/components/Collection";
import Alchemy from "@/components/Alchemy";
import Manifesto from "@/components/Manifesto";
import SocialProof from "@/components/SocialProof";
import Ritual from "@/components/Ritual";
import Footer from "@/components/Footer";
import OrderModal from "@/components/OrderModal";
import ScrollAnimations from "@/components/ScrollAnimations";
import RevealObserver from "@/components/RevealObserver";
import MagneticButton from "@/components/MagneticButton";
import AmbientCanvas from "@/components/AmbientCanvas";
import NextImage from "next/image";

interface Category {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  image: string;
  accent: string;
  productCount: number;
  href: string;
}

const categories: Category[] = [
  {
    id: "pour-femme",
    name: "Pour Femme",
    subtitle: "Feminine Signature",
    description: "Night-blooming florals wrapped in incense and dark amber.",
    image: "/images/images%20(3).webp",
    accent: "#c47a8b",
    productCount: 1,
    href: "/categories"
  },
  {
    id: "pour-homme",
    name: "Pour Homme",
    subtitle: "Masculine Signature",
    description: "Smoke, earth, and sinew — leather and oud noir.",
    image: "/images/images%20(4).webp",
    accent: "#5b7d9b",
    productCount: 1,
    href: "/categories"
  },
  {
    id: "alchemy",
    name: "Alchemy",
    subtitle: "Raw Ingredients",
    description: "The soul of each scent, sourced from volcanic highlands.",
    image: "/images/banner-1.webp",
    accent: "#8a6a2c",
    productCount: 12,
    href: "/categories"
  }
];

function CategoryCard({ category, index }: { category: Category; index: number }) {
  return (
    <Link
      href={category.href}
      className="relative group gold-frame gold-glow-hover reveal-blur p-6 md:p-8 overflow-hidden min-h-[400px] md:min-h-[440px] flex flex-col"
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <AmbientCanvas
        variant="mist"
        accent={category.accent}
        density={0.3}
        className="absolute inset-0 opacity-30 mix-blend-multiply pointer-events-none"
        aria-hidden="true"
      />
      <div
        className="orb-glow -bottom-1/4 -right-1/4 w-[20rem] h-[20rem] md:w-[24rem] md:h-[24rem] opacity-50 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${category.accent}1a 0%, transparent 65%)` }}
        aria-hidden="true"
      />

      <div className="relative flex flex-col flex-1">
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-6 h-px md:w-8" style={{ backgroundColor: category.accent }} />
            <span className="text-[8px] md:text-[9px] tracking-[0.3em] uppercase" style={{ color: category.accent }}>
              {category.subtitle}
            </span>
          </div>
          <span className="font-mono text-[8px] md:text-[9px] tracking-[0.2em] uppercase text-[#6f6759]/50">
            {category.productCount} {category.productCount === 1 ? "Scent" : "Scents"}
          </span>
        </div>

        <div className="relative flex-1 flex items-center justify-center my-6 min-h-0">
          <div className="relative w-full max-w-md mx-auto">
            <div
              className="aspect-[4/5] relative overflow-hidden rounded-[2px] bg-gradient-to-br"
              style={{
                background: `linear-gradient(135deg, ${category.accent}15 0%, ${category.accent}05 100%)`
              }}
            >
              <NextImage
                src={category.image}
                alt={category.name}
                fill
                className="object-cover"
                sizes="(max-width: 640px) 90vw, (max-width: 1024px) 50vw, 25vw"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 via-transparent to-transparent" />
            </div>
            <div className="silk left-[-10%] top-[4%] w-[20rem] h-[14rem] md:w-[24rem] md:h-[18rem] opacity-35 pointer-events-none"
              style={{
                background: `radial-gradient(ellipse at center, ${category.accent}2e, ${category.accent}08 55%, transparent 75%)`
              }}
              aria-hidden="true"
            />
          </div>
        </div>

        <div className="border-t border-[#221d17]/10 pt-6 mt-auto">
          <h3 className="font-serif text-2xl md:text-3xl text-[#221d17] mb-3" style={{ fontWeight: 300, lineHeight: 1.1 }}>
            {category.name}
          </h3>
          <p className="text-xs md:text-sm text-[#6f6759]/70 italic mb-5 max-w-md leading-relaxed">{category.description}</p>

          <MagneticButton strength={0.15}>
            <button className="btn-elegant w-full sm:w-auto" data-cursor="pointer">
              <span>Explore {category.name}</span>
            </button>
          </MagneticButton>
        </div>
      </div>
    </Link>
  );
}

export default function Home() {
  const [loaded, setLoaded] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [orderModalOpen, setOrderModalOpen] = useState(false);
  const [preselectedProduct, setPreselectedProduct] = useState<string | undefined>();

  const handlePreloaderComplete = useCallback(() => {
    setLoaded(true);
    requestAnimationFrame(() => {
      setShowContent(true);
    });
  }, []);

  const openOrderModal = useCallback((product?: string) => {
    setPreselectedProduct(product);
    setOrderModalOpen(true);
  }, []);

  return (
    <>
      {!loaded && <Preloader onComplete={handlePreloaderComplete} />}

      {/* Film-grain texture overlay */}
      <div
        className="bg-noise fixed inset-0 z-[9997] pointer-events-none opacity-[0.04]"
        aria-hidden="true"
      />

      <SmoothScroll>
        <main
          role="main"
          aria-label="STYGIAN Perfumes"
          className={`transition-[opacity,filter,transform] duration-1000 ${
            showContent ? "opacity-100 blur-0 scale-100" : "opacity-0 page-blur"
          }`}
        >
          <Navigation onOrderClick={() => openOrderModal()} />

          {/* Scroll progress bar */}
          <div
            className="fixed top-0 left-0 right-0 z-[60] h-[2px] pointer-events-none"
            role="progressbar"
            aria-label="Scroll progress"
          >
            <div
              className="scroll-progress-line h-full origin-left"
              style={{
                background: "linear-gradient(90deg, rgba(201,168,76,0.2), #c9a84c, rgba(201,168,76,0.2))",
                transform: "scaleX(0)",
              }}
            />
          </div>

          <div className="relative z-10">
            <section id="hero" aria-label="Hero">
              <Hero onOrderClick={() => openOrderModal()} />
            </section>

            <Ticker />

            <section id="collection" aria-label="Fragrance Collection">
              <Collection onOrderClick={(product) => openOrderModal(product)} />
            </section>

            {/* Browse Categories */}
            <section id="browse-categories" aria-label="Browse Categories" className="relative py-24 md:py-32 overflow-hidden" style={{ background: "linear-gradient(180deg, #f2ecdf 0%, #efe9df 100%)" }}>
              <div className="absolute inset-0 marble opacity-40" aria-hidden="true" />
              <div className="hairline-grid absolute inset-0 opacity-15" aria-hidden="true" />

              <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
                <div className="mb-16 md:mb-20">
                  <p className="reveal-blur text-[10px] tracking-[0.4em] uppercase text-[#8a6a2c]/70 mb-4">
                    Browse by Category
                  </p>
                  <h2 className="reveal-blur font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight text-[#221d17]" style={{ fontWeight: 300, transitionDelay: "0.1s", lineHeight: 1.05 }}>
                    Discover by
                    <br />
                    <span className="text-shimmer-gold">Essence</span>
                  </h2>
                  <div className="section-divider w-16 h-px mt-8 bg-gradient-to-r from-[#c9a84c]/60 to-transparent" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
                  {categories.map((category, index) => (
                    <CategoryCard key={category.id} category={category} index={index} />
                  ))}
                </div>
              </div>
            </section>

            <section id="alchemy" aria-label="Ingredients">
              <Alchemy />
            </section>

            <section id="creed" aria-label="The Stygian Creed">
              <Manifesto />
            </section>

            <section id="voices" aria-label="Testimonials">
              <SocialProof />
            </section>

            <section id="ritual" aria-label="The Ritual">
              <Ritual onOrderClick={() => openOrderModal()} />
            </section>

            <Footer />
          </div>
        </main>
      </SmoothScroll>

      <OrderModal
        isOpen={orderModalOpen}
        onClose={() => setOrderModalOpen(false)}
        product={preselectedProduct}
      />

      <ScrollAnimations />
      <RevealObserver />
    </>
  );
}